// devtools/DeveloperConsole.jsx
import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";

export default function DeveloperConsole({ logs, clearLogs }) {
  const consoleRef = useRef(null);
  const scrollRef = useRef(null);
  const [pos, setPos] = useState({
    x: window.innerWidth - 420,
    y: window.innerHeight - 320,
  });
  const [size, setSize] = useState({ width: 400, height: 260 });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState(null);

  const COLORS = {
    success: "#2E7D32",
    error: "#D32F2F",
    warn: "#ED6C02",
    info: "#0288D1",
  };

  const ICONS = {
    success: (
      <CheckCircleIcon sx={{ color: COLORS.success, fontSize: 11, mr: 0.8 }} />
    ),
    error: <ErrorIcon sx={{ color: COLORS.error, fontSize: 11, mr: 0.8 }} />,
    warn: <WarningIcon sx={{ color: COLORS.warn, fontSize: 11, mr: 0.8 }} />,
    info: <InfoIcon sx={{ color: COLORS.info, fontSize: 11, mr: 0.8 }} />,
  };

  // 🧭 Move logic
  const handleMouseDown = e => {
    if (e.target.closest(".resize-handle")) return;
    setDragging(true);
    setDragOffset({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };

  // ↔️ Resize logic
  const handleResizeMouseDown = e => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    setResizeStart({
      mouseX: e.clientX,
      mouseY: e.clientY,
      width: size.width,
      height: size.height,
    });
  };

  const handleMouseMove = e => {
    if (dragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      const maxX = window.innerWidth - size.width;
      const maxY = window.innerHeight - size.height;
      setPos({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }

    if (resizing && resizeStart) {
      const dx = e.clientX - resizeStart.mouseX;
      const dy = e.clientY - resizeStart.mouseY;
      const newWidth = Math.max(320, resizeStart.width + dx);
      const newHeight = Math.max(200, resizeStart.height + dy);
      setSize({ width: newWidth, height: newHeight });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
    setResizing(false);
    setResizeStart(null);
  };

  // 🎯 Attach global listeners for smooth resize/drag
  useEffect(() => {
    if (dragging || resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [dragging, resizing, dragOffset, resizeStart, size, pos]);

  // 🎢 Auto-scroll to latest log
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [logs]);

  // 🎹 Toggle shortcut
  const toggleConsole = () => {
    window.dispatchEvent(
      new KeyboardEvent("keydown", { ctrlKey: true, shiftKey: true, key: "l" })
    );
  };

  return (
    <Box
      ref={consoleRef}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: size.width,
        height: size.height,
        bgcolor: "#ffffff",
        color: "#222",
        border: "1px solid #E0E0E0",
        borderRadius: 2,
        boxShadow: 5,
        overflow: "hidden",
        zIndex: 1300,
        fontFamily: "Inter",
        fontSize: "12px",
        userSelect: dragging || resizing ? "none" : "auto",
        cursor: dragging ? "grabbing" : "default",
        transition: "box-shadow 0.2s ease",
      }}
    >
      {/* Header */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#F5F5F5",
          p: "4px 8px",
          cursor: "grab",
          borderBottom: "1px solid #E0E0E0",
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 600, fontSize: "12px" }}
        >
          🧠 Developer Logs
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button
            size='small'
            onClick={clearLogs}
            sx={{
              color: "#666",
              textTransform: "none",
              fontSize: "11px",
              minWidth: "auto",
              mr: 1,
            }}
          >
            Clear
          </Button>
          <IconButton
            size='small'
            sx={{ color: "#666" }}
            onClick={toggleConsole}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      </Box>

      {/* Logs Area */}
      <Box
        ref={scrollRef}
        sx={{
          height: `calc(${size.height}px - 40px)`,
          overflowY: "auto",
          p: 1,
          scrollBehavior: "smooth",
        }}
      >
        {logs.length === 0 ? (
          <Typography
            variant='body2'
            color='gray'
            sx={{ fontSize: "12px", textAlign: "center", mt: 2 }}
          >
            No logs yet...
          </Typography>
        ) : (
          logs.map((log, i) => (
            <Fade in timeout={300} key={i}>
              <Tooltip title={log.message} arrow placement='top-start'>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "#f9f9f9",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {ICONS[log.type]}
                    <Typography
                      variant='body2'
                      sx={{
                        color: COLORS[log.type],
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "11px",
                      }}
                    >
                      {log.message}
                    </Typography>
                  </Box>
                  <Typography
                    variant='caption'
                    sx={{
                      color: "#999",
                      ml: 1,
                      flexShrink: 0,
                      fontSize: "9px",
                    }}
                  >
                    {log.time}
                  </Typography>
                </Box>
              </Tooltip>
            </Fade>
          ))
        )}
      </Box>

      {/* Resize Handle */}
      <Box
        className='resize-handle'
        onMouseDown={handleResizeMouseDown}
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: 20,
          height: 20,
          cursor: "se-resize",
          bgcolor: "#DADADA",
          borderRadius: "2px",
          "&:hover": { bgcolor: "#BDBDBD" },
        }}
      />
    </Box>
  );
}
